import { apiClient } from './client'
import { ApiResponse } from './types'
import { withIncludeTest } from '@/utils/includeTestHelper'
import type { ContractBadgeData } from './affiliation-contract.api'

export interface CommissionRule {
  id: string
  legacy_id: number | null
  rule_type: string
  priority: number
  conditions: Record<string, any> | null
  commission_rate: number | null
  revshare_override: number | null
  coupon_code: string | null
  days_of_week: number[] | null
  status: string
  valid_from: string | null
  valid_until: string | null
  created_by: string | null
  locally_modified_at: string | null
  created_at: string
  updated_at: string
}

/**
 * #1184 — the Contract popup's payload. Every optional key is OMITTED by the
 * server when the underlying value is empty, so the modal hides a field by
 * simply not finding it. `legacyText` is the fallback for affiliations with no
 * commission_rules row yet: the old raw contract text, shown in one block.
 */
export interface ContractDetails {
  affiliationId: string
  hotelId: string
  affiliateId: string
  hotelName?: string
  affiliateName?: string
  status?: string
  ruleId?: string
  partnerCommissionPercent?: number
  discountCode?: string
  followersDiscountPercent?: number
  stayPeriod?: { from?: string; to?: string }
  validDays?: { mode: 'all' } | { mode: 'days'; days: string[] }
  excludedDates?: Array<{
    startDate: string
    endDate: string
    reason?: string
    repeatYearly?: boolean
  }>
  specialConditions?: string
  createdBy?: string
  legacyText?: string
}

interface PendingAffiliationRequest {
  requestId: string
  affiliate: {
    id: string
    name: string
    email?: string
    avatar?: string
    bio?: string
    followers?: number
    platform?: string
  }
  requestedAt: string
  message?: string
}

interface ActiveAffiliation {
  affiliateId: string
  name: string
  email?: string
  avatar?: string
  commissionRate: number
  totalBookings: number
  totalRevenue: number
  status: 'active' | 'paused'
  joinedAt: string
  // #359 follow-up: expiration lifecycle surfaced to the list so the
  // modal can open with current values and the row can render an
  // "expires in Nd" badge.
  expiresAt?: string | null
  autoRenew?: boolean
  renewalPeriodDays?: number
}

interface ActiveAffiliationsResponse {
  data: ActiveAffiliation[]
  pagination?: {
    total: number
    page: number
    limit: number
    pages: number
  }
}

interface HandleAffiliationRequestPayload {
  action: 'accept' | 'decline'
  commissionRate?: number
  // #459: optional reason captured from the reject-reason modal.
  reason?: string
}

interface InviteAffiliatePayload {
  // inviteAffiliateSchema on the server treats every field as optional;
  // at least one of affiliateEmail/affiliateId must be supplied but the
  // validator itself doesn't require `email` or `commissionRate`.
  email?: string
  message?: string
  commissionRate?: number
}

interface UpdateAffiliationPayload {
  commissionRate?: number
  status?: string
  hotelNotes?: string
}

interface AffiliationStatsSummary {
  total: number
  live: number
  approved: number
  pending: number
  declined: number
  // #1951: Ended affiliations — their own KPI card, no longer in `declined`.
  // Optional: a response from a server without #1951 has no such field.
  ended?: number
  // Unique affiliate_id count across the result set (vs `total` which is
  // row-count of hotel_affiliation_statuses). Used by /hotel-affiliations
  // 'Total Affiliates' KPI so admin viewers see ~74 unique affiliates
  // instead of 2718 cross-hotel relationship rows.
  uniqueAffiliates?: number
}

export interface AffiliationStatsRow {
  affiliationId: string
  /** #2087: the hotel is paused (no new partner); the row stays listed. */
  hotelIsPaused?: boolean
  partnershipId: string
  affiliateId: string
  // #731: YYYY-MM-DD expiry of the commission override in force for this pair,
  // and whether it falls inside the warning window. Optional so a cached
  // response from before this field existed still typechecks.
  overrideValidUntil?: string | null
  overrideExpiringSoon?: boolean
  /** #1361: the Contract badge, derived from the pair's commission rules. */
  contractBadge?: ContractBadgeData | null
  hotelId?: string | null
  hotelName?: string | null
  affiliateLegacyId?: string | null
  fullName?: string
  companyName?: string
  displayName?: string
  contactEmail?: string | null
  affiliateNotes?: string | null
  hotelNotes?: string | null
  // #678: original invite/request message, shown in the Pending-Your-Decision modal.
  pendingDecisionMessage?: string | null
  hotelPublicNotes?: string | null
  affiliatePublicNotes?: string | null
  // #329 comment-11: caller's OWN per-user private note for this affiliation.
  userNoteHtml?: string | null
  statusForHotels?: string | null
  affiliationStatus?: string
  addOverrideLink?: string | null
  hotelConfirmLink?: string | null
  affiliateConfirmLink?: string | null
  socialAccounts?: {
    website?: string | null
    facebook?: string | null
    instagram?: string | null
    youtube?: string | null
    tiktok?: string | null
    twitter?: string | null
    linkedin?: string | null
    whatsapp?: string | null
    other_channel?: string | null
  }
  businessType?: string | null
  affiliateType?: string | null
  // #284 — how this affiliate arrived (e.g. 'white_label_embed' vs default platform signup).
  source?: string | null
  logoUrl?: string | null
  // #763: affiliate Top Media so the details modal can render the gallery.
  topMedia?: Array<{ id: string; type: 'image' | 'video' | 'link'; url: string; title?: string; views?: number }>
  createdAt?: string
  commissionRate?: number | null
  contract?: string | null
  baseCommission?: number | null
  affiliationLinks?: Array<{ url: string; type: string }> | null
  // #469 Slice 4: derived contract envelope from the affiliations-list
  // LEFT JOIN. null when no non-terminated contract exists for the pair.
  contractStatus?: {
    id: string
    derivedStatus:
      | 'Draft'
      | 'AwaitingSender'
      | 'AwaitingAffiliate'
      | 'Signed'
      | 'Rejected'
      | 'Cancelled'
      | 'None'
    hotelSignerName: string | null
    hotelSignedAt: string | null
    affiliateSignerName: string | null
    affiliateSignedAt: string | null
  } | null
}

interface AffiliationStatsResponse {
  summary: AffiliationStatsSummary
  affiliations: AffiliationStatsRow[]
  // #488: pagination envelope. Legacy callers that don't pass `limit`
  // still get the full dataset, with pagination.total === affiliations.length.
  pagination?: { total: number; page: number; limit: number; pages: number }
}

// #642 Section B — chain / multi-hotel affiliations grid (READ-ONLY foundation).
// #1951: 'Ended' — the chain grid shows an Ended affiliation as Ended, not Declined.
export type ChainStatusBucket = 'Live' | 'Approved' | 'Pending' | 'Declined' | 'Ended'
// #766: 'reapprove' = the hotel's "Change your mind?" on its own decline.
export type ChainGridAction = 'approve' | 'reject' | 'send_contract' | 'update_contract' | 'reapprove'

export interface ChainGridHotelRow {
  hotelId: string
  hotelName: string
  affiliationId: string
  // Raw status_for_hotels value (shown verbatim; the FE localizes via i18n).
  status: string
  statusBucket: ChainStatusBucket
  contract: string | null
  // #642 B.2 — commission rate for this (hotel, affiliate) pair, mirrors the
  // single-hotel table's Contract column (renders as a percentage, not the
  // raw contract blob). Optional: the backend pending-grid query does not yet
  // SELECT base_commission, so this is undefined until that field is added
  // (see PartnershipService.getChainAffiliationGrid). Rendered as em-dash when
  // absent, exactly like the single-hotel column does for missing commissions.
  baseCommission?: number | null
  // #731: commission-override expiry, per hotel. Each hotel in a chain carries
  // its own override, so one row can warn while its siblings do not.
  overrideValidUntil?: string | null
  overrideExpiringSoon?: boolean
  /** #1361: the Contract badge, derived from the pair's commission rules. */
  contractBadge?: ContractBadgeData | null
  // #798: the caller's OWN per-user Internal Note for this affiliation —
  // drives the green "has a note" ring on the grid's NotesCell (parity with
  // the single-hotel table). Optional for back-compat with older responses.
  userNoteHtml?: string | null
  // #799: affiliate's original invite/request message — drives the pink "?"
  // badge on Pending-your-decision rows (parity with the single-hotel table).
  pendingMessage?: string | null
  // #284 — how this affiliate arrived (e.g. 'white_label_embed' vs default platform signup).
  source?: string | null
  contractStatus: AffiliationStatsRow['contractStatus']
  availableActions: ChainGridAction[]
}

export interface ChainGridAffiliate {
  affiliateId: string
  name: string
  platforms: string[]
  // #642 B.2 (3) — full social handle map for clickable platform links,
  // mirroring AffiliationStatsRow.socialAccounts.
  socialAccounts?: {
    website?: string | null
    facebook?: string | null
    instagram?: string | null
    youtube?: string | null
    tiktok?: string | null
    twitter?: string | null
    linkedin?: string | null
    whatsapp?: string | null
    other_channel?: string | null
  }
  // #642 B.2 (4) — raw affiliate_type string (parsed by the FE type-badge helper).
  affiliateType?: string | null
  affiliatePublicNotes: string | null
  avatar: string | null
  // #821 — affiliate top media for the details-modal gallery (parity with the
  // single-hotel table's row.topMedia).
  topMedia?: Array<{ id: string; type: 'image' | 'video' | 'link'; url: string; title?: string; views?: number }>
  bestStatus: ChainStatusBucket
  hotelCount: number
  isMultiHotel: boolean
  actionsHomogeneous: boolean
  mainRowActions: ChainGridAction[]
  hotels: ChainGridHotelRow[]
}

export interface ChainAffiliationGrid {
  chainId: string
  chainName: string
  memberHotelCount: number
  affiliates: ChainGridAffiliate[]
}

// #642 E5a — per-hotel outcome of a chain approve/reject fan-out.
export interface ChainDecisionOutcome {
  hotelId: string
  hotelName: string
  outcome: 'applied' | 'skipped' | 'error'
  reason?: string
}

interface ChainDecisionResult {
  outcomes: ChainDecisionOutcome[]
}

/**
 * #1663 status categories (partner-service HAS_STATUS_CATEGORIES): the
 * server's classification of an affiliation's status text.
 */
export type AffiliationStatusCategory =
  | 'live'
  | 'approved'
  | 'awaiting_contract'
  | 'pending'
  | 'suggested'
  | 'declined'
  | 'contract_rejected'
  | 'terminated'
  | 'ended'
  | 'hidden'
  | 'other'

export const hotelAffiliationsApi = {
  getAffiliations: (
    hotelId: string,
    params?: { page?: number; limit?: number; search?: string; affiliateId?: string; status?: string }
  ): Promise<ApiResponse<ActiveAffiliationsResponse>> =>
    apiClient.get(`/hotels/${hotelId}/affiliations`, { params: withIncludeTest(params ?? {}) }),
  getPendingAffiliations: (hotelId: string): Promise<ApiResponse<PendingAffiliationRequest[]>> =>
    apiClient.get(`/hotels/${hotelId}/affiliations/pending`, { params: withIncludeTest({}) }),
  // #642 E2 — grouped (by affiliate) affiliations grid across every hotel in
  // a chain. Spans pending + live/approved/declined. READ-ONLY foundation.
  // #978: hotelId narrows the grid to one chain hotel. Sent to the BE rather than
  // filtered client-side because the per-affiliate aggregates (bestStatus,
  // hotelCount, isMultiHotel, availableActions) and the KPI cards are all
  // computed server-side over the queried rows.
  getChainAffiliationGrid: (
    chainId: string,
    hotelId?: string,
  ): Promise<ApiResponse<ChainAffiliationGrid>> =>
    apiClient.get(`/chains/${chainId}/affiliations/pending-grid`, {
      params: withIncludeTest(hotelId ? { hotelId } : {}),
    }),
  // #642 E5a — approve/reject one affiliate across every PENDING hotel in the
  // chain. Returns per-hotel outcomes (applied/skipped/error).
  decideChainAffiliation: (
    chainId: string,
    affiliateId: string,
    action: 'approve' | 'reject',
  ): Promise<ApiResponse<ChainDecisionResult>> =>
    apiClient.put(`/chains/${chainId}/affiliates/${affiliateId}/decision`, { action }),
  handlePendingAffiliation: (
    hotelId: string,
    requestId: string,
    payload: HandleAffiliationRequestPayload
  ): Promise<ApiResponse<{ success: boolean; message: string }>> =>
    apiClient.put(`/hotels/${hotelId}/affiliations/pending/${requestId}`, payload),
  inviteAffiliate: (
    hotelId: string,
    payload: InviteAffiliatePayload
  ): Promise<ApiResponse<{ success: boolean; message: string }>> =>
    apiClient.post(`/hotels/${hotelId}/affiliations/invite`, payload),
  updateAffiliation: (
    hotelId: string,
    // Despite the backend's route name, this is the hotel_affiliation_statuses
    // row id (the affiliation), not the affiliate's UUID — passing an
    // affiliate UUID 404s.
    affiliationId: string,
    payload: UpdateAffiliationPayload
  ): Promise<ApiResponse<{ success: boolean; message: string }>> =>
    apiClient.put(`/hotels/${hotelId}/affiliations/${affiliationId}`, payload),
  getAffiliationStats: (
    hotelId: string,
    filters?: {
      status?: string
      // #488: server-side pagination + sort + search.
      search?: string
      categories?: string[]
      // #1095: affiliate narrowing is server-side, so a selected affiliate is
      // matched across every page instead of only the one being displayed.
      affiliateId?: string
      sortField?: 'affiliate' | 'status' | 'contract' | 'created_at'
      sortDirection?: 'asc' | 'desc'
      page?: number
      limit?: number
    },
  ): Promise<ApiResponse<AffiliationStatsResponse>> => {
    const params: Record<string, string | number> = {}
    if (filters?.status) params.status = filters.status
    if (filters?.affiliateId) params.affiliate_id = filters.affiliateId
    if (filters?.search) params.search = filters.search
    if (filters?.categories && filters.categories.length > 0) {
      params.categories = filters.categories.join(',')
    }
    if (filters?.sortField) params.sort_field = filters.sortField
    if (filters?.sortDirection) params.sort_direction = filters.sortDirection
    if (filters?.page) params.page = filters.page
    if (filters?.limit) params.limit = filters.limit
    return apiClient.get(`/hotels/${hotelId}/affiliation-stats`, { params: withIncludeTest(params) })
  },

  /**
   * Batched variant — single request scoped to multiple hotels via the
   * existing `/hotels/reports/affiliation-stats` endpoint. Used by admin
   * dashboard flows (HotelPerformancePage, HotelPerformanceSalesPage,
   * HotelVisibilityPage, HotelReservationsPage) where the user has
   * access to many hotels and fanning one request per hotel hit the
   * rate limiter (429).
   *
   * Returns the same {summary, affiliations} shape as the single-hotel
   * variant — backend aggregates across all requested hotels.
   */
  getBatchAffiliationStats: (
    hotelIds: string[],
    params?: { status?: string; sortBy?: string; sortOrder?: 'ASC' | 'DESC' }
  ): Promise<ApiResponse<AffiliationStatsResponse>> => {
    const query: Record<string, string> = { hotel_ids: hotelIds.join(',') }
    if (params?.status) query.status = params.status
    if (params?.sortBy) query.sort_by = params.sortBy
    if (params?.sortOrder) query.sort_order = params.sortOrder
    return apiClient.get('/hotels/reports/affiliation-stats', { params: withIncludeTest(query) })
  },

  createCommissionOverride: (
    hotelId: string,
    affiliationId: string,
    data: {
      commissionPercentage: number | null
      affiliateVisitDate?: string
      stayDateFrom?: string
      stayDateTo?: string
      bookingDateFrom?: string
      bookingDateTo?: string
      // Handler defaults to 'all' when applicableDays is omitted; coupon
      // requirement defaults to false. Keep them optional on the wire.
      applicableDays?: 'all' | 'select'
      selectedDays?: string[]
      couponCodeRequired?: boolean
      couponCode?: string
      discountForFollowers?: number
      specialConditions?: string
      filledByName: string
      // #457: chain fan-out — when true on a chain-parent hotel, backend
      // applies the same override to every sub-hotel under chain_hotels.
      applyToChain?: boolean
      // #1185 rule 2: also mint a sibling rule pinning the REMAINING weekdays
      // to 0%. Only honoured when applicableDays === 'select'.
      zeroOnRemainingDays?: boolean
      // #1185 rule 3: opt in to rebasing the affiliation's base commission to
      // the entered rate. Previously this happened unconditionally server-side.
      updateBaseCommission?: boolean
    }
  ): Promise<ApiResponse<any>> =>
    apiClient.post(`/hotels/${hotelId}/affiliations/${affiliationId}/commission-override`, data),
  listCommissionRules: (
    hotelId: string,
    affiliationId: string
  ): Promise<ApiResponse<CommissionRule[]>> =>
    apiClient.get(`/hotels/${hotelId}/affiliations/${affiliationId}/commission-rules`),
  // #1184 — structured contract terms for the read-only Contract popup.
  // Absent fields are omitted by the server, so the modal's "hide when missing"
  // is a plain truthiness check on each key.
  getContractDetails: (
    hotelId: string,
    affiliationId: string
  ): Promise<ApiResponse<ContractDetails>> =>
    apiClient.get(`/hotels/${hotelId}/affiliations/${affiliationId}/contract-details`),
  getMultiHotelAffiliationStats: (params: {
    hotelId?: string
    hotelIds?: string[]
    // #493: filters must round-trip on the multi-hotel admin path too.
    // Previously only `status` was forwarded — status tabs, search, category
    // chips, sort headers, and pagination were all dropped on the wire,
    // so the page rendered the full unfiltered dataset.
    status?: string
    // #488 + Bug 10: forward the same filter surface the backend
    // exposes on /hotels/reports/affiliation-stats so admin "All Hotels"
    // mode in HotelActiveAffiliationsPage can actually filter by status,
    // search, categories, sort, and pagination. Prior shape only
    // forwarded status + a sortBy/sortOrder pair that the controller
    // doesn't read (it expects sort_field/sort_direction), so the
    // admin status KPI cards were no-ops — clicking "Live" returned
    // the unfiltered set.
    search?: string
    categories?: string[]
    // #1095: see getAffiliationStats — server-side affiliate narrowing.
    affiliateId?: string
    sortField?: 'affiliate' | 'status' | 'contract' | 'created_at'
    sortDirection?: 'asc' | 'desc'
    page?: number
    limit?: number
  }): Promise<ApiResponse<AffiliationStatsResponse>> => {
    const query: Record<string, string | number> = {}
    if (params.hotelId) query.hotel_id = params.hotelId
    if (params.hotelIds && params.hotelIds.length > 0) query.hotel_ids = params.hotelIds.join(',')
    if (params.status) query.status = params.status
    if (params.affiliateId) query.affiliate_id = params.affiliateId
    if (params.search) query.search = params.search
    if (params.categories && params.categories.length > 0) query.categories = params.categories.join(',')
    if (params.sortField) query.sort_field = params.sortField
    if (params.sortDirection) query.sort_direction = params.sortDirection
    if (params.page) query.page = params.page
    if (params.limit) query.limit = params.limit
    return apiClient.get('/hotels/reports/affiliation-stats', { params: withIncludeTest(query) })
  },

  // #329 Slice C — per-user private notes (hotel side). Empty string when
  // the caller hasn't written one yet — never 404, matches the backend
  // contract that returns noteHtml: ''.
  getUserNotes: (
    hotelId: string,
    affiliationId: string,
  ): Promise<ApiResponse<{ noteHtml: string }>> =>
    apiClient.get(`/hotels/${hotelId}/affiliations/${affiliationId}/user-notes`),

  updateUserNotes: (
    hotelId: string,
    affiliationId: string,
    noteHtml: string,
  ): Promise<ApiResponse<{ success: true }>> =>
    apiClient.put(`/hotels/${hotelId}/affiliations/${affiliationId}/user-notes`, { noteHtml }),

  // #495 — Hotel-side contact-details lookup. Backend returns 403 when the
  // affiliation status is not Live/Approved, so this is also the source of
  // truth for "can we even open the modal" — callers should pre-check the
  // row's status in the FE for UX (don't show a clickable name on
  // non-approved rows) but the modal's catch block handles the 403 too.
  getAffiliateContact: (
    hotelId: string,
    affiliateId: string,
  ): Promise<ApiResponse<{
    displayName: string
    companyName: string | null
    contactName: string
    email: string | null
    phone: string | null
    alternatePhone: string | null
  }>> => apiClient.get(`/hotels/${hotelId}/affiliations/${affiliateId}/contact`),

  /**
   * #1676 — how many of one hotel's affiliations are in each status category,
   * for the admin hotel page. Reads the summary of the #1663 admin affiliations
   * list, so the counts come from the same server-side classifier as the admin
   * Affiliations page and include every affiliation (hidden and ended ones
   * too). Admin-only endpoint; limit=1 because only the summary is used.
   */
  getStatusCategoryCounts: async (
    hotelId: string,
    opts?: { includeTest?: boolean },
  ): Promise<Partial<Record<AffiliationStatusCategory, number>>> => {
    const params = withIncludeTest({
      hotelIds: hotelId,
      limit: 1,
      ...(opts?.includeTest ? { includeTest: 'true' } : {}),
    })
    const res = await apiClient.get<{ summary?: { byStatus?: Partial<Record<AffiliationStatusCategory, number>> } }>(
      '/admin/affiliations',
      { params },
    )
    return res.data?.summary?.byStatus ?? {}
  },
}
